

import React, { useState, useEffect, useCallback } from "react";
import Navbar from "../Navigation/Navbar";
import Auth from "../Common/Configurations/Auth";
import APIConfig from "../Common/Configurations/APIConfig";
import { 
  Layers, Map as MapIcon, Users, UserCheck, 
  TrendingUp, Clock, Loader2, CalendarDays, 
  ChevronRight, Activity
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const userName = Auth.getFullName();
  
  // --- STATES ---
  const [isSyncing, setIsSyncing] = useState(true);
  const [stats, setStats] = useState({ projects: 0, plots: 0, members: 0, agents: 0 });
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [recentMembers, setRecentMembers] = useState([]);

  // --- HELPERS ---
  const dateString = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
  });

  // --- INSTANT DATA FETCHING ---
  const fetchDashboardData = useCallback(async () => {
    try {
      // Parallel Fetching with small PageSize=5 for maximum speed
      const [projRes, plotRes, memRes, agentRes, receiptRes] = await Promise.all([
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PROJECT_GET_ALL}?pageSize=1`),
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.PLOT_GET_ALL}?pageSize=1`),
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_GET_ALL}?pageNumber=1&pageSize=5`), 
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.AGENT_GET_ALL}?pageSize=1`),
        fetch(`${APIConfig.APIACTIVEURL}${APIConfig.RECEIPT_GET_ALL}?pageNumber=1&pageSize=5`) 
      ]);

      const [projData, plotData, memData, agentData, receiptData] = await Promise.all([
        projRes.json(), plotRes.json(), memRes.json(), agentRes.json(), receiptRes.json()
      ]);

      setStats({
        projects: projData.totalCount || 0,
        plots: plotData.totalCount || 0,
        members: memData.totalCount || 0,
        agents: agentData.totalCount || 0
      });

      setRecentReceipts(receiptData.data || []);
      setRecentMembers(memData.data || []);
    } catch (error) {
      console.error("Dashboard Sync Error:", error);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const styles = {
    container: { padding: "40px 60px", backgroundColor: "#f4f7fe", minHeight: "calc(100vh - 70px)", fontFamily: "'Inter', sans-serif" },
    welcomeHeader: { 
      background: "linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)", 
      padding: "40px", borderRadius: "30px", color: "#fff", marginBottom: "35px",
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
    },
    statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "25px", marginBottom: "40px" },
    glassPanel: { 
      backgroundColor: "#fff", borderRadius: "28px", padding: "35px", 
      boxShadow: "0 10px 30px rgba(0,0,0,0.02)", border: '1px solid #f1f5f9', minHeight: '300px', overflow: 'hidden' 
    },
    tableHeaderLabel: { fontSize: '11px', fontWeight: '800', color: '#a3aed0', textTransform: 'uppercase', letterSpacing: '0.5px' }
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        
        {/* HEADER */}
        <div style={styles.welcomeHeader}>
          <div>
            <h1 style={{ margin: 0, fontSize: "32px", fontWeight: "900", letterSpacing: '-1px' }}>Welcome, {userName}</h1>
            <p style={{ opacity: 0.8, fontSize: '16px', fontWeight: '500', marginTop: '8px' }}>Manage your real estate portfolio from your central dashboard.</p>
          </div>
          
          <div style={{ textAlign: 'right', background: 'rgba(255,255,255,0.1)', padding: '15px 25px', borderRadius: '20px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CalendarDays size={20} />
                <span style={{ fontSize: '16px', fontWeight: '800' }}>{dateString}</span>
            </div>
          </div>
        </div>

        {/* TOP STATS */}
        <div style={styles.statGrid}>
          <StatCard icon={<Layers />} title="Total Projects" value={stats.projects} color="#4f46e5" loading={isSyncing} />
          <StatCard icon={<MapIcon />} title="Total Plots" value={stats.plots} color="#10b981" loading={isSyncing} />
          <StatCard icon={<Users />} title="Members" value={stats.members} color="#6366f1" loading={isSyncing} />
          <StatCard icon={<UserCheck />} title="Active Agents" value={stats.agents} color="#f59e0b" loading={isSyncing} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: '30px' }}>
          
          {/* LATEST 5 TRANSACTIONS WITH HEADERS */}
          <div style={{ ...styles.glassPanel, padding: '35px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', padding: '0 35px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '900', color: '#1b2559', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Activity color="#4f46e5" size={24} /> Recent Transactions
                </h3>
                <button onClick={() => navigate('/receipts')} style={{ border: 'none', background: '#f4f7fe', color: '#4338ca', padding: '10px 18px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer', fontSize: '13px' }}>
                    View All
                </button>
            </div>
            
            {/* COLUMN HEADERS - FIXED AS PER PIC 2 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 1fr', padding: '15px 35px', borderBottom: '1.5px solid #f8fafc', background: '#fcfcfd' }}>
                <span style={styles.tableHeaderLabel}>Receipt Details</span>
                <span style={styles.tableHeaderLabel}>Member</span>
                <span style={styles.tableHeaderLabel}>Amount</span>
                <span style={{ ...styles.tableHeaderLabel, textAlign: 'right' }}>Mode</span>
            </div>

            {isSyncing ? (
                <div style={{ display:'flex', justifyContent:'center', padding:'50px' }}><Loader2 className="animate-spin" color="#4338ca" /></div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '0 35px' }}>
                    {recentReceipts.map((r, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.5fr 1fr 1fr', padding: '20px 0', borderBottom: '1px solid #f8fafc', alignItems: 'center' }}>
                            <span style={{ fontWeight: '800', color: '#1b2559', fontSize: '14px' }}>{r.receiptNo}</span>
                            <span style={{ color: '#707eae', fontWeight: '600', fontSize: '14px' }}>{r.member?.memberName}</span>
                            <span style={{ color: '#059669', fontWeight: '900', fontSize: '15px' }}>₹{r.amount.toLocaleString()}</span>
                            <span style={{ color: '#4338ca', fontWeight: '800', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                                {r.paymentMode || 'Cash'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            {/* LATEST 5 MEMBERS */}
            <div style={styles.glassPanel}>
                <h3 style={{ fontSize: '20px', fontWeight: '900', color: '#1b2559', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Clock color="#f59e0b" size={24} /> New Members
                </h3>
                {isSyncing ? (
                    <div style={{ display:'flex', justifyContent:'center', padding:'50px' }}><Loader2 className="animate-spin" color="#f59e0b" /></div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
                        {recentMembers.map((m, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                    <div style={{ width: '45px', height: '45px', borderRadius: '14px', background: '#f4f7fe', color: '#4338ca', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900' }}>
                                        {m.memberName?.charAt(0)}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '800', color: '#1b2559', fontSize: '14px' }}>{m.memberName}</div>
                                    
                                    </div>
                                </div>
                                <ChevronRight size={18} color="#cbd5e1" />
                            </div>
                        ))}
                    </div>
                )}
                <button onClick={() => navigate('/members')} style={{ width: '100%', marginTop: '30px', padding: '16px', borderRadius: '16px', border: '2.5px solid #f4f7fe', background: 'transparent', color: '#1b2559', fontWeight: '800', cursor: 'pointer' }}>
                    View Directory
                </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

// --- SUB-COMPONENTS ---
const StatCard = ({ icon, title, value, color, loading }) => (
    <div style={{ backgroundColor: "#fff", padding: "28px", borderRadius: "28px", boxShadow: "0 10px 30px rgba(0,0,0,0.02)", display: 'flex', alignItems: 'center', gap: '20px', border: '1px solid #f1f5f9' }}>
        <div style={{ width: '60px', height: '60px', borderRadius: '18px', backgroundColor: `${color}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: color }}>
            {React.cloneElement(icon, { size: 28 })}
        </div>
        <div>
            <div style={{ color: "#a3aed0", fontSize: "11px", fontWeight: "800", textTransform: "uppercase", letterSpacing: '1px' }}>{title}</div>
            {loading ? (
                <div style={{ marginTop: '5px' }}><Loader2 size={16} className="animate-spin" color={color} /></div>
            ) : (
                <div style={{ color: "#1b2559", fontSize: "26px", fontWeight: "900", marginTop: '2px' }}>{value}</div>
            )}
        </div>
    </div>
);

export default Dashboard;